import React from 'react';

// components
import {Skeleton} from '@mui/joy';
import {CardBorder} from '../../CardBorder';

// styles
import {
  DesktopWrapper,
  DesktopContent,
  CardColumn,
  Separator,
  DesktopCircleWrapper,
} from '../CardDetails-styles';

// hooks
import {useTheme} from '@emotion/react';
import {LabelContentHorizontalSkeleton} from '../../LabelContentHorizontal';
import {Columns} from './types';

type Variant = 'summary' | 'detailed';

// types
type Props = {
  columns: Columns;
  variant: Variant;
};

/**
 * Split columns into two sections based on the variant
 * For 'summary':
 *   Section 1: We take the first 4
 *   Section 2: We take the next 3 since the last row will be a skeleton text
 * For 'detailed':
 *   Section 1: We take the first 6
 *   Section 2: We take the next 6
 * @param columns columns to split
 * @param variant variant to determine how to split the columns
 * @returns An object containing two sections of columns
 */
function splitColumns(columns: Columns, variant: Variant) {
  const section1 =
    variant === 'summary' ? columns.slice(0, 4) : columns.slice(0, 6);
  const section2 =
    variant === 'summary' ? columns.slice(4, 7) : columns.slice(6, 12);

  return {section1, section2};
}

const CardDetailsSkeletonDesktop = ({columns, variant}: Props) => {
  const {skeleton} = useTheme();

  const {section1, section2} = splitColumns(columns, variant);

  return (
    <CardBorder variant="white" hasShadow>
      <DesktopWrapper>
        {variant === 'summary' && (
          <Skeleton variant="text" width={skeleton.text.width.xxl} />
        )}
        <DesktopContent>
          <CardColumn>
            {section1.map(({headerWidth, contentWidth}, index) => (
              <LabelContentHorizontalSkeleton
                key={index}
                headerWidth={headerWidth}
                contentWidth={contentWidth}
                isFullWidth
              />
            ))}
          </CardColumn>
          <Separator />
          <CardColumn>
            {section2.map(({headerWidth, contentWidth}, index) => (
              <LabelContentHorizontalSkeleton
                key={index}
                headerWidth={headerWidth}
                contentWidth={contentWidth}
                isFullWidth
              />
            ))}
            {variant === 'summary' && (
              <Skeleton variant="text" width={skeleton.text.width.md} />
            )}
          </CardColumn>
          <Separator />
          <DesktopCircleWrapper>
            <Skeleton variant="circular" width={142} height={142} />
          </DesktopCircleWrapper>
        </DesktopContent>
      </DesktopWrapper>
    </CardBorder>
  );
};

export default CardDetailsSkeletonDesktop;
